import { useState, useCallback, useEffect } from "react";
import { Play } from "lucide-react";
import { cn } from "@/lib/utils";

function usePreconnect(urls: string[]) {
  useEffect(() => {
    const links: HTMLLinkElement[] = [];
    urls.forEach(url => {
      const existingLink = document.querySelector(`link[rel="preconnect"][href="${url}"]`);
      if (!existingLink) {
        const link = document.createElement("link");
        link.rel = "preconnect";
        link.href = url;
        document.head.appendChild(link);
        links.push(link);
      }
    });
    return () => {
      links.forEach(link => link.remove());
    };
  }, [urls]);
}

interface VideoFacadeProps {
  videoUrl: string;
  provider: "youtube" | "vimeo" | "other";
  thumbnail?: string;
  title?: string;
  duration?: number;
  className?: string;
  aspectRatio?: "16:9" | "4:3" | "1:1";
  autoPlay?: boolean;
}

function extractVideoId(url: string, provider: "youtube" | "vimeo" | "other"): string | null {
  if (provider === "youtube") {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
      /youtube\.com\/shorts\/([^&\s?]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
  } else if (provider === "vimeo") {
    const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (match) return match[1];
  }
  return null;
}

function getYouTubeThumbnail(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}

function getVimeoThumbnail(videoId: string): string {
  return `https://vumbnail.com/${videoId}.jpg`;
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

export function VideoFacade({
  videoUrl,
  provider,
  thumbnail,
  title,
  duration,
  className,
  aspectRatio = "16:9",
  autoPlay = false,
}: VideoFacadeProps) {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  
  const preconnectUrls = provider === "youtube" 
    ? ["https://www.youtube.com", "https://i.ytimg.com"]
    : provider === "vimeo"
      ? ["https://player.vimeo.com"]
      : [];
  
  usePreconnect(preconnectUrls);
  
  const videoId = extractVideoId(videoUrl, provider);
  
  const thumbnailUrl = thumbnail || (
    provider === "youtube" && videoId ? getYouTubeThumbnail(videoId) :
    provider === "vimeo" && videoId ? getVimeoThumbnail(videoId) :
    undefined
  );
  
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);
  
  const aspectRatioClass = {
    "16:9": "aspect-video",
    "4:3": "aspect-[4/3]",
    "1:1": "aspect-square"
  }[aspectRatio];
  
  const getEmbedUrl = useCallback((muted = false) => {
    if (!videoId) return videoUrl;
    
    if (provider === "youtube") {
      const params = new URLSearchParams({
        autoplay: "1",
        rel: "0",
        modestbranding: "1",
        origin: window.location.origin,
      });
      if (muted) params.set("mute", "1");
      return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
    } else if (provider === "vimeo") {
      const params = new URLSearchParams({ autoplay: "1" });
      if (muted) params.set("muted", "1");
      return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
    }
    return videoUrl;
  }, [videoId, provider, videoUrl]);
  
  const watchUrl = provider === "youtube" && videoId
    ? `https://www.youtube.com/watch?v=${videoId}`
    : videoUrl;

  if (isPlaying) {
    return (
      <div className={cn("w-full", className)}>
        <div className={cn(aspectRatioClass, "w-full bg-black rounded-lg overflow-hidden")}>
          <iframe
            src={getEmbedUrl(autoPlay)}
            title={title || "Video"}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
        <div className="mt-2 text-center">
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
          >
            Watch on YouTube ↗
          </a>
        </div>
      </div>
    );
  }
  
  return (
    <button
      onClick={handlePlay}
      className={cn(
        aspectRatioClass,
        "w-full relative group cursor-pointer rounded-lg overflow-hidden bg-slate-900",
        className
      )}
      data-testid="video-facade-play"
      aria-label={title ? `Play video: ${title}` : "Play video"}
    >
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt={title || "Video thumbnail"}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
      )}
      
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
      
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-lg">
          <Play className="w-8 h-8 md:w-10 md:h-10 text-slate-900 ml-1" fill="currentColor" />
        </div>
      </div>
      
      {duration && (
        <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/80 text-white text-sm font-medium rounded">
          {formatDuration(duration)}
        </div>
      )}
      
      {title && (
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
          <h3 className="text-white font-medium text-lg truncate">{title}</h3>
        </div>
      )}
    </button>
  );
}

export function VideoFacadeSkeleton({ aspectRatio = "16:9" }: { aspectRatio?: "16:9" | "4:3" | "1:1" }) {
  const aspectRatioClass = {
    "16:9": "aspect-video",
    "4:3": "aspect-[4/3]",
    "1:1": "aspect-square"
  }[aspectRatio];
  
  return (
    <div className={cn(aspectRatioClass, "w-full rounded-lg bg-muted animate-pulse")} />
  );
}
